import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "../graphql/mutations";
import * as eventSlot from "./EventSlot";
import moment from "moment";

export class Slot {
  constructor({
    room,
    capacity,
    assignedTo,
    start,
    end,
    dates,
    active,
    location,
    type,
    slot,
    bloodDropStart,
    bloodDropEnd,
    copiedSlotGroups,
  }) {
    this.room = room;
    this.capacity = capacity;
    this.assignedTo = assignedTo;
    this.start = start;
    this.end = end;
    this.dates = dates;
    this.active = active;
    this.location = location;
    this.type = type;
    this.slot = slot;
    this.bloodDropStart = bloodDropStart;
    this.bloodDropEnd = bloodDropEnd;
    this.copiedSlotGroups = copiedSlotGroups;
  }

  async reserveWorkshop() {
    const that = this;
    const workshopGroup = (date) => {
      return new Promise(async (resolve, reject) => {
        that.createGroupWorkshop(date).then((group) => {
          resolve(group);
        });
      });
    };
    const groups = await Promise.all(
      this.dates.map(async (date) => await workshopGroup(date))
    );
    return groups;
  }

  async createGroupWorkshop(date) {
    const that = this;
    return new Promise(function (resolve, reject) {
      API.graphql(
        graphqlOperation(mutations.createEventSlotGroup, {
          input: {
            room: that.room,
            date: date,
            capacity: that.capacity,
            assignedTo: that.assignedTo,
            active: that.active,
            createdAt: new Date().toISOString(),
            isDeleted: 0,
            eventSlotGroupEventLocationId: that.location,
          },
        })
      ).then((result) => {
        const slotGroup = {
          id: result.data.createEventSlotGroup.id,
          date: result.data.createEventSlotGroup.date,
          room: result.data.createEventSlotGroup.room,
          capacity: result.data.createEventSlotGroup.capacity,
          assignedTo: JSON.parse(result.data.createEventSlotGroup.assignedTo),
          active: result.data.createEventSlotGroup.active,
          slots: [],
          filter: false,
        };
        that
          .createWorkshopSlot(result.data.createEventSlotGroup.id)
          .then((result) => {
            slotGroup.slots.push(result);
            resolve(slotGroup);
          });
      });
    });
  }

  async createWorkshopSlot(slotGroupId) {
    const that = this;
    let startTime = moment(this.start, "HH:mm");
    let endTime = moment(this.end, "HH:mm");
    let duration = endTime.diff(startTime);
    let length = duration / (1000 * 60);

    let slotBatches = [];
    slotBatches.push({
      time: startTime.format("HH:mm") + " - " + endTime.format("HH:mm"),
      length: parseInt(length),
      type: that.type,
      eventSlotEventSlotGroupId: slotGroupId,
      booked: 0,
      isDeleted: 0,
    });

    const createBatchEventSlot = (batch) => {
      return new Promise(async (resolve) => {
        API.graphql(
          graphqlOperation(mutations.createBatchEventSlot, {
            slots: batch,
          })
        ).then((result) => {
          resolve(result.data.createBatchEventSlot);
        });
      });
    };

    // Run slot batches
    const createdSlots = await Promise.all(
      slotBatches.map(async (batch) => await createBatchEventSlot(batch))
    );

    return createdSlots;
  }

  async reserveBookingSlot() {
    const that = this;
    const bookingSlotGroup = (date) => {
      return new Promise(async (resolve, reject) => {
        that.BulkCreateGroupBookingSlot(date).then((group) => {
          resolve(group);
        });
      });
    };
    const groups = await Promise.all(
      this.dates.map(async (date) => await bookingSlotGroup(date))
    );
    return groups;
  }

  async createGroupBookingSlot(date) {
    const that = this;

    const createBookingSlot = (slotGroupId, slot) => {
      return new Promise(async (resolve) => {
        API.graphql(
          graphqlOperation(mutations.createEventSlot, {
            input: {
              time: slot.start + " - " + slot.end,
              length: parseInt(slot.length),
              type: slot.type,
              eventSlotEventSlotGroupId: slotGroupId,
              booked: 0,
              isDeleted: 0,
            },
          })
        ).then((result) => {
          resolve(result.data.createEventSlot);
        });
      });
    };

    // return new Promise(function (resolve, reject) {
    const result = await API.graphql(
      graphqlOperation(mutations.createEventSlotGroup, {
        input: {
          room: that.room,
          date: date,
          capacity: that.capacity,
          assignedTo: that.assignedTo,
          active: that.active,
          createdAt: new Date().toISOString(),
          isDeleted: 0,
          eventSlotGroupEventLocationId: that.location,
        },
      })
    );

    const slotGroup = {
      id: createdSlotGroup.id,
      date: createdSlotGroup.date,
      room: createdSlotGroup.room,
      capacity: createdSlotGroup.capacity,
      assignedTo: JSON.parse(createdSlotGroup.assignedTo),
      active: createdSlotGroup.active,
      slots: [],
      filter: false,
    };

    let slots;
    switch (that.slot) {
      case 5:
        slots = eventSlot.fiveMinuteSlots;
        break;
      case 15:
        slots = eventSlot.fifteenMinuteSlots;
        break;
      case 20:
        slots = eventSlot.twentyMinuteSlots;
        break;
      case 30:
        slots = eventSlot.thirtyMinuteSlots;
        break;
      case 45:
        slots = eventSlot.fortyfiveMinuteSlots;
        break;
      case 60:
        slots = eventSlot.hourSlots;
        break;
      case "custom":
        slots = [];
        break;
      case "copy":
        slots = that.copiedSlotGroups;
        break;
      default:
        slots = [];
    }

    let start = this.start;
    start = moment(start, "HH:mm");
    let end = moment(start, "HH:mm");
    const bloodDropIn = this.bloodDropEnd && this.bloodDropStart ? true : false;

    if (bloodDropIn === false) {
      // Remove blood slot
      slots = slots.filter((slot) => slot.type !== "BLOOD");
    }

    slots = slots.map((slot, index) => {
      if (index === 0) {
        end.add(slot.length, "minutes");
        return {
          ...slot,
          start: start.format("HH:mm"),
          end: end.format("HH:mm"),
          time: start.format("HH:mm") + "-" + end.format("HH:mm"),
        };
      } else {
        start.add(slots[index - 1].length, "minutes");
        end.add(slot.length, "minutes");
        return {
          ...slot,
          start: start.format("HH:mm"),
          end: end.format("HH:mm"),
          time: start.format("HH:mm") + "-" + end.format("HH:mm"),
        };
      }
    });

    const slotsCreated = await Promise.all(
      slots.map(
        async (slot) => await createBookingSlot(createdSlotGroup.id, slot)
      )
    );
    // slotsCreated.forEach(function (slot) {
    //   // this.getSlotColour();                      
    //   slotGroup.slots.push({ ...slot, ...colour });
    // });

    return slotsCreated;
  }

  async BulkCreateGroupBookingSlot(date) {
    const that = this;

    let slots;
    let colour;
    const bloodDropIn = this.bloodDropEnd && this.bloodDropStart ? true : false;
    switch (that.slot) {
      case 5:
        slots = eventSlot.fiveMinuteSlots;
        colour = { colour: "error" };
        break;
      case 15:
        slots = eventSlot.fifteenMinuteSlots;
        colour = { colour: "pink lighten-1" };
        break;
      case 20:
        slots = eventSlot.twentyMinuteSlots;
        colour = { colour: "purple darken-1" };
        break;
      case 30:
        slots = eventSlot.thirtyMinuteSlots;
        colour = { colour: "indigo lighten-1" };
        break;
      case 35:
        slots = eventSlot.thirtyFiveMinuteSlots;
        colour = { colour: "amber darken-1" };
        break;
      case 45:
        if (bloodDropIn) {
          slots = eventSlot.fortyfiveMinuteSlotsWithBlood;
        } else {
          slots = eventSlot.fortyfiveMinuteSlots;
        }
        colour = { colour: "indigo accent-2" };
        break;
      case 60:
        if (bloodDropIn) {
          slots = eventSlot.hourSlotsWithBlood;
        } else {
          slots = eventSlot.hourSlots;
        }
        colour = { colour: "cyan darken-2" };
        break;
      case "copy":
        colour = { colour: "info" };
        slots = that.copiedSlotGroups;
        break;
      case "custom":
        colour = { colour: "info" };
        slots = [];
        break;
      default:
        colour = { colour: "info" };
        slots = [];
    }

    let start = this.start;
    start = moment(start, "HH:mm");
    let end = moment(start, "HH:mm");

    // if (bloodDropIn === false) {
    //   // Remove blood slot
    //   slots = slots.filter((slot) => slot.type !== "BLOOD");
    // }

    slots = slots.map((slot, index) => {
      if (index === 0) {
        end.add(slot.length, "minutes");
        return {
          ...slot,
          start: start.format("HH:mm"),
          end: end.format("HH:mm"),
          time: start.format("HH:mm") + "-" + end.format("HH:mm"),
        };
      } else {
        start.add(slots[index - 1].length, "minutes");
        end.add(slot.length, "minutes");
        return {
          ...slot,
          start: start.format("HH:mm"),
          end: end.format("HH:mm"),
          time: start.format("HH:mm") + "-" + end.format("HH:mm"),
        };
      }
    });

    // Create group
    const {
      data: { createEventSlotGroup: createdSlotGroup },
    } = await API.graphql(
      graphqlOperation(mutations.createEventSlotGroup, {
        input: {
          room: that.room,
          date: date,
          capacity: that.capacity,
          assignedTo: that.assignedTo,
          active: that.active,
          createdAt: new Date().toISOString(),
          isDeleted: 0,
          eventSlotGroupEventLocationId: that.location,
        },
      })
    );

    const bulkSlots = slots.map((slot) => {
      return {
        booked: 0,
        type: slot.type,
        eventSlotEventSlotGroupId: createdSlotGroup.id,
        isDeleted: 0,
        length: slot.length,
        time: `${slot.start} - ${slot.end}`,
      };
    });

    const chunk = (arr, size) =>
      arr.reduce(
        (acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]),
        []
      );

    // Batch into chunks of 25
    const slotBatches = chunk(bulkSlots, 25);

    const createBatchEventSlot = (batch) => {
      return new Promise(async (resolve) => {
        API.graphql(
          graphqlOperation(mutations.createBatchEventSlot, {
            slots: batch,
          })
        ).then((result) => {
          resolve(result.data.createBatchEventSlot);
        });
      });
    };

    // Run slot batches
    const createdSlots = await Promise.all(
      slotBatches.map(async (batch) => await createBatchEventSlot(batch))
    );

    // Slot Group
    const slotGroup = {
      id: createdSlotGroup.id,
      date: createdSlotGroup.date,
      room: createdSlotGroup.room,
      addressLine1: createdSlotGroup.eventLocation.addressLine1,
      town: createdSlotGroup.eventLocation.town,
      capacity: createdSlotGroup.capacity,
      assignedTo: JSON.parse(createdSlotGroup.assignedTo),
      slots: [],
      filter: false,
    };

    // Add Slots and Colours
    createdSlots.forEach(function (slots) {
      slots.forEach(function (slot) {
        slotGroup.slots.push({ ...slot, ...colour });
      });
    });

    return slotGroup;
  }

  // getSlotColour(length) {
  //   let colour = {};
  //   switch (length) {
  //     case 5:
  //       colour = { colour: "error" };
  //       break;
  //     case 15:
  //       colour = { colour: "pink lighten-1" };
  //       break;
  //     case 20:
  //       colour = { colour: "purple darken-1" };
  //       break;
  //     case 30:
  //       colour = { colour: "indigo lighten-1" };
  //       break;
  //     case 35:
  //       colour = { colour: "amber darken-1" };
  //       break;
  //     case 45:
  //       colour = { colour: "indigo accent-2" };
  //       break;
  //     case 60:
  //       colour = { colour: "cyan darken-2" };
  //       break;
  //     default:
  //       colour = { colour: "info" };
  //   }
  //   return colour;
  // }
}
